import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  type ScratchCell,
  type ScratchEventResponse,
  type ScratchOffer,
  type ScratchOfferPrize,
  type ScratchPrize,
  type ScratchState,
  type ScratchSymbol,
  type ScratchTicket,
  type ScratchTicketDto,
} from '@/common/types/scratch';

/**
 * Prize table — prizes the player can choose as their target reward when
 * scratching. Weights govern the auto-suggest randomization for filler
 * cells; rarity drives the visual treatment.
 */
const PRIZE_TABLE: ScratchPrize[] = [
  { rewardType: 'cash', value: 5_000, weight: 22, rarity: 'common' },
  { rewardType: 'cash', value: 50_000, weight: 12, rarity: 'common' },
  { rewardType: 'dice', value: 5, weight: 14, rarity: 'common' },
  { rewardType: 'tgem', value: 25, weight: 10, rarity: 'rare' },
  { rewardType: 'starPoints', value: 250, weight: 8, rarity: 'rare' },
  { rewardType: 'silverTicket', value: 1, weight: 6, rarity: 'rare' },
  { rewardType: 'goldTicket', value: 1, weight: 3, rarity: 'epic' },
  { rewardType: 'tgem', value: 200, weight: 2, rarity: 'epic' },
  { rewardType: 'bingoBalls', value: 25, weight: 2, rarity: 'epic' },
  { rewardType: 'diamondTicket', value: 1, weight: 1, rarity: 'legendary' },
  { rewardType: 'tgem', value: 1_000, weight: 0.5, rarity: 'legendary' },
];

function symbolFromPrize(
  prize: ScratchPrize | ScratchOfferPrize,
  idSuffix: string,
): ScratchSymbol {
  return {
    id: `${prize.rewardType}-${prize.value}-${idSuffix}`,
    rewardType: prize.rewardType,
    rarity: prize.rarity,
    value: prize.value,
  };
}

function rewardKey(p: { rewardType: string; value: number }): string {
  return `${p.rewardType}-${p.value}`;
}

function shufflePositions(): number[] {
  const a = [0, 1, 2];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }

  return a;
}

/**
 * Roll how many of the chosen reward will appear on the 3-cell grid.
 * Returns 1 (guaranteed floor), 2, or 3 according to the offer's
 * `pTwo`/`pThree` chances.
 */
function rollMatchCount(offer: ScratchOffer): 1 | 2 | 3 {
  const r = Math.random();

  if (r < offer.pThree) return 3;

  if (r < offer.pThree + offer.pTwo) return 2;

  return 1;
}

/**
 * Build a 3-cell ticket row given a guaranteed selected reward and the
 * desired match count. The matched cells share the selected reward; the
 * remaining slots are filled with non-matching rewards drawn from the
 * offer's own pool — keeps the visual identity of the pack consistent
 * (cheap pack shows cheap fillers, premium pack shows premium fillers).
 */
function pickRandom<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function buildTicketCells(
  selected: ScratchOfferPrize,
  matchCount: 1 | 2 | 3,
  pool: ScratchOfferPrize[],
): ScratchCell[] {
  const positions = shufflePositions();
  const cells: ScratchCell[] = Array.from({ length: 3 });

  // Place matchCount copies of the selected reward.
  for (let i = 0; i < matchCount; i++) {
    cells[positions[i]] = {
      index: positions[i],
      symbol: symbolFromPrize(selected, `m-${i}`),
    };
  }

  // Fillers come from the same offer pool (uniform draw, excluding the
  // selected reward). With a tightly scoped pool there's no need for
  // the global weight system here.
  const others = pool.filter((p) => rewardKey(p) !== rewardKey(selected));
  const fillerSource =
    others.length > 0
      ? others
      : PRIZE_TABLE.filter((p) => rewardKey(p) !== rewardKey(selected));
  for (let i = matchCount; i < 3; i++) {
    const filler = pickRandom(fillerSource);
    cells[positions[i]] = {
      index: positions[i],
      symbol: symbolFromPrize(filler, `f-${i}`),
    };
  }

  return cells;
}

function rollTicket(serial: number, offer: ScratchOffer): ScratchTicket | null {
  if (!offer.selectedRewardKey) return null;
  const selected = offer.rewardPool.find(
    (p) => rewardKey(p) === offer.selectedRewardKey,
  );

  if (!selected) return null;

  const matchCount = rollMatchCount(offer);
  const cells = buildTicketCells(selected, matchCount, offer.rewardPool);

  return {
    id: `t-${serial}-${Math.random().toString(36).slice(2, 8)}`,
    serial,
    cells,
    selectedRewardKey: offer.selectedRewardKey,
    matchCount,
    prize: null,
    status: 'idle',
    progress: 0,
    offerId: offer.id,
  };
}

/**
 * Multiplier odds — IDENTICAL across all three offers. Players don't get
 * better win chances for paying more; they get a better *pool* of rewards
 * to choose from. (Average payout ≈ 1.40× the chosen reward.)
 */
const SHARED_P_TWO = 0.3;
const SHARED_P_THREE = 0.1;

const INITIAL_OFFERS: ScratchOffer[] = [
  {
    id: 'ads',
    paid: false,
    amountStars: 0,
    amountUsd: 0,
    purchasesLeft: 1,
    maxPurchases: 1,
    frame: 'ads',
    pTwo: SHARED_P_TWO,
    pThree: SHARED_P_THREE,
    available: true,
    rewardPool: [
      { rewardType: 'cash', value: 1000, rarity: 'common' },
      { rewardType: 'tgem', value: 5, rarity: 'common' },
      { rewardType: 'dice', value: 1, rarity: 'common' },
      { rewardType: 'starPoints', value: 20, rarity: 'common' },
      { rewardType: 'bingoBalls', value: 2, rarity: 'common' },
    ],
    selectedRewardKey: null,
  },
  {
    id: 'basic',
    paid: true,
    amountStars: 76,
    amountUsd: 0.99,
    purchasesLeft: 3,
    maxPurchases: 3,
    frame: 'gold',
    pTwo: SHARED_P_TWO,
    pThree: SHARED_P_THREE,
    available: true,
    rewardPool: [
      { rewardType: 'cash', value: 5000, rarity: 'common' },
      { rewardType: 'tgem', value: 10, rarity: 'common' },
      { rewardType: 'dice', value: 3, rarity: 'common' },
      { rewardType: 'starPoints', value: 50, rarity: 'common' },
      { rewardType: 'bingoBalls', value: 5, rarity: 'common' },
      { rewardType: 'silverTicket', value: 1, rarity: 'rare' },
    ],
    selectedRewardKey: null,
  },
  {
    id: 'standard',
    paid: true,
    amountStars: 153,
    amountUsd: 1.99,
    purchasesLeft: 3,
    maxPurchases: 3,
    frame: 'silver',
    pTwo: SHARED_P_TWO,
    pThree: SHARED_P_THREE,
    available: true,
    rewardPool: [
      { rewardType: 'cash', value: 15000, rarity: 'rare' },
      { rewardType: 'tgem', value: 30, rarity: 'rare' },
      { rewardType: 'dice', value: 8, rarity: 'rare' },
      { rewardType: 'starPoints', value: 150, rarity: 'rare' },
      { rewardType: 'bingoBalls', value: 15, rarity: 'rare' },
      { rewardType: 'goldTicket', value: 1, rarity: 'epic' },
    ],
    selectedRewardKey: null,
  },
  {
    id: 'premium',
    paid: true,
    amountStars: 384,
    amountUsd: 4.99,
    purchasesLeft: 3,
    maxPurchases: 3,
    frame: 'crimson',
    pTwo: SHARED_P_TWO,
    pThree: SHARED_P_THREE,
    available: true,
    rewardPool: [
      { rewardType: 'cash', value: 50000, rarity: 'epic' },
      { rewardType: 'tgem', value: 100, rarity: 'epic' },
      { rewardType: 'dice', value: 25, rarity: 'epic' },
      { rewardType: 'starPoints', value: 500, rarity: 'epic' },
      { rewardType: 'bingoBalls', value: 50, rarity: 'epic' },
      { rewardType: 'diamondTicket', value: 1, rarity: 'legendary' },
    ],
    selectedRewardKey: null,
  },
];

/**
 * Demo event window — placeholder until the GET /scratch/event endpoint
 * is wired. Mocks an "active" event ending in ~6 hours so the countdown
 * HUD and the Game Center card timer have something to render.
 */
function buildDemoEvent(): ScratchState['event'] {
  const now = Date.now();
  const sixHours = 6 * 60 * 60 * 1000;

  return {
    id: 'demo-scratch-event',
    status: 'active',
    startAt: new Date(now - 60 * 60 * 1000).toISOString(),
    endAt: new Date(now + sixHours).toISOString(),
    now,
    multiplierOdds: { pTwo: SHARED_P_TWO, pThree: SHARED_P_THREE },
  };
}

const initialState: ScratchState = {
  active: [],
  offers: INITIAL_OFFERS,
  revealedCount: 0,
  pendingClaimTicketId: null,
  event: buildDemoEvent(),
};

/**
 * Convert server `ScratchTicketDto` into the in-state `ScratchTicket`.
 * Defensive: if a field the client expects is missing (server shape
 * drift, partial WS payload, etc.) we substitute safe defaults instead
 * of throwing inside the reducer.
 */
function ticketFromDto(dto: ScratchTicketDto): ScratchTicket {
  const cells = Array.isArray(dto.cells)
    ? dto.cells.map((c) => ({
        index: c.index,
        symbol: {
          id: `${c.symbol.rewardType}-${c.symbol.value}-${c.index}`,
          rewardType: c.symbol.rewardType,
          value: c.symbol.value,
          rarity: c.symbol.rarity,
        },
      }))
    : [];

  return {
    id: dto.id,
    serial: dto.serial,
    cells,
    selectedRewardKey: dto.selectedRewardKey ?? '',
    matchCount: dto.matchCount ?? 1,
    prize: dto.prize ?? null,
    status: dto.status ?? 'idle',
    progress: dto.status === 'revealed' || dto.status === 'claimed' ? 1 : 0,
    offerId: dto.offerId,
    autoRevealAt: dto.autoRevealAt,
  };
}

const scratchSlice = createSlice({
  name: 'scratch',
  initialState,
  reducers: {
    /** Player picked (or cleared) a target reward for a specific offer. */
    selectOfferReward: (
      state,
      {
        payload,
      }: PayloadAction<{
        offerId: ScratchOffer['id'];
        rewardKey: string | null;
      }>,
    ) => {
      const offer = state.offers.find((o) => o.id === payload.offerId);

      if (offer) offer.selectedRewardKey = payload.rewardKey;
      // also reflect it on this offer's active ticket if there is one
      const t = state.active.find((t) => t.offerId === payload.offerId);

      if (t && payload.rewardKey) t.selectedRewardKey = payload.rewardKey;
    },

    /**
     * Buy a ticket from one of the offers. Local fallback used by the
     * `ads` synchronous response path; paid flows go through the buy
     * mutation + WS / refetch.
     */
    buyTicket: (
      state,
      { payload }: PayloadAction<{ offerId: ScratchOffer['id'] }>,
    ) => {
      const offer = state.offers.find((o) => o.id === payload.offerId);

      if (!offer || offer.purchasesLeft <= 0) return;

      if (!offer.selectedRewardKey) return;

      // Each offer can host one in-progress ticket at a time.
      if (state.active.some((t) => t.offerId === offer.id)) return;

      const serial = state.revealedCount + 1;
      const ticket = rollTicket(serial, offer);

      if (!ticket) return;
      offer.purchasesLeft -= 1;
      state.active.push(ticket);
    },

    /**
     * Update scratch coverage on the ticket bound to a specific offer.
     * Surface uses `offerId` because the canvas component knows its offer
     * but doesn't pass ticket id around.
     */
    setProgress: (
      state,
      {
        payload,
      }: PayloadAction<{ offerId: ScratchOffer['id']; progress: number }>,
    ) => {
      const t = state.active.find((t) => t.offerId === payload.offerId);

      if (!t) return;
      t.progress = Math.max(t.progress, Math.min(1, payload.progress));

      if (t.status === 'idle' && t.progress > 0) t.status = 'scratching';
    },

    /** Local fallback reveal (used only if mutation is bypassed). */
    revealActive: (state, { payload }: PayloadAction<{ ticketId: string }>) => {
      const t = state.active.find((x) => x.id === payload.ticketId);

      if (!t || t.status === 'revealed' || t.status === 'claimed') return;
      t.status = 'revealed';
      t.progress = 1;

      const matches = t.cells.filter(
        (c) => rewardKey(c.symbol) === t.selectedRewardKey,
      ).length;
      const owningOffer = state.offers.find((o) => o.id === t.offerId);
      const selected = owningOffer?.rewardPool.find(
        (p) => rewardKey(p) === t.selectedRewardKey,
      );

      if (selected && matches > 0) {
        t.prize = {
          rewardType: selected.rewardType,
          value: selected.value * matches,
        };
      } else {
        t.prize = null;
      }
      state.pendingClaimTicketId = t.id;
      state.revealedCount += 1;
    },

    /** Player dismissed the result modal — drop the claimed ticket. */
    claimAndClose: (state) => {
      const id = state.pendingClaimTicketId;
      state.pendingClaimTicketId = null;

      if (!id) return;
      state.active = state.active.filter((t) => t.id !== id);
    },

    /** Reset offer counters (e.g., when daily reset hits). */
    resetOffers: (state) => {
      state.offers = INITIAL_OFFERS.map((o) => ({ ...o }));
    },

    /**
     * Hydrate the slice from a fresh `GET /scratch/event` payload.
     * Server is the source of truth for offer counters, event timing,
     * and the active ticket. Client-only `selectedRewardKey` is preserved
     * across reloads so the player's pre-buy choice doesn't get wiped.
     */
    initFromApi: (state, { payload }: PayloadAction<ScratchEventResponse>) => {
      // Preserve client picker selections by offerId across the hydrate.
      const previousSelections: Record<string, string | null> = {};
      for (const o of state.offers) {
        previousSelections[o.id] = o.selectedRewardKey;
      }

      // Server might omit fields during dev — fall back to current state
      // rather than wiping it.
      if (Array.isArray(payload.offers)) {
        state.offers = payload.offers.map((dto) => ({
          ...dto,
          selectedRewardKey: previousSelections[dto.id] ?? null,
        }));
      }

      state.event = {
        id: payload.id ?? state.event.id,
        status: payload.status ?? state.event.status,
        startAt: payload.startAt ?? state.event.startAt,
        endAt: payload.endAt ?? state.event.endAt,
        now: payload.now ?? state.event.now,
        multiplierOdds: payload.multiplierOdds ?? state.event.multiplierOdds,
      };

      if (typeof payload.revealedCount === 'number') {
        state.revealedCount = payload.revealedCount;
      }

      // Server returns an array of in-progress tickets (≤1 per offer).
      // Preserve client-side scratch progress for tickets that survive the
      // hydrate (same id) so an in-flight refetch doesn't reset the foil.
      if (Array.isArray(payload.active)) {
        const prevById = new Map<string, number>();
        for (const t of state.active) prevById.set(t.id, t.progress);
        state.active = payload.active.map((dto) => {
          const t = ticketFromDto(dto);
          const carried = prevById.get(t.id);

          if (typeof carried === 'number' && t.status === 'idle') {
            t.progress = carried;

            if (carried > 0) t.status = 'scratching';
          }

          return t;
        });
      }
    },

    /**
     * Drop a freshly-bought ticket into state — used after the synchronous
     * `ads` purchase response or after `SCRATCH_TICKET_BOUGHT` on a paid flow.
     *
     * Idempotent: if the ticket is already in state.active (e.g. it arrived
     * via WS and a subsequent refetch hydrated the same one — or vice
     * versa), we skip the optimistic `purchasesLeft` decrement so the
     * counter doesn't go negative. Server payload from the next
     * `/scratch/event` refetch is the canonical source either way.
     */
    applyBoughtTicket: (
      state,
      { payload }: PayloadAction<ScratchTicketDto>,
    ) => {
      const idx = state.active.findIndex((t) => t.id === payload.id);

      if (idx >= 0) {
        // Idempotent: refresh ticket fields, don't re-decrement counters.
        state.active[idx] = ticketFromDto(payload);

        return;
      }
      state.active.push(ticketFromDto(payload));
      const offer = state.offers.find((o) => o.id === payload.offerId);

      if (offer && offer.purchasesLeft > 0) {
        offer.purchasesLeft -= 1;
        offer.available = offer.purchasesLeft > 0;
      }
    },

    /**
     * Replace the in-state ticket with the server-claimed version after
     * `POST /scratch/reveal` resolves. Server has the canonical prize.
     */
    applyRevealedTicket: (
      state,
      { payload }: PayloadAction<ScratchTicketDto>,
    ) => {
      const idx = state.active.findIndex((t) => t.id === payload.id);

      if (idx < 0) return;
      state.active[idx] = ticketFromDto(payload);
      state.pendingClaimTicketId = payload.id;
      state.revealedCount += 1;
    },
  },
});

export const {
  selectOfferReward,
  buyTicket,
  setProgress,
  revealActive,
  claimAndClose,
  resetOffers,
  initFromApi,
  applyBoughtTicket,
  applyRevealedTicket,
} = scratchSlice.actions;
export { PRIZE_TABLE };
export default scratchSlice.reducer;
